import { ChevronDown, Mail, Search } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { gmailFindEmail } from 'sprites-types/workflow/tools/gmailFindEmail'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function EmailItem({ email, className }: { email: any; className?: string }) {
  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className={`rounded-full p-2 ${email.unread ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <Mail className={`h-4 w-4 ${email.unread ? 'text-blue-600' : 'text-gray-600'}`} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div
              className={`text-sm ${email.unread ? 'font-semibold' : 'font-medium'} truncate text-gray-900`}
            >
              {email.subject || 'No subject'}
            </div>
            {email.unread && <div className="h-2 w-2 rounded-full bg-blue-500" />}
          </div>
          <div className="mt-1 text-xs text-gray-600">
            From: {email.from || email.sender || 'Unknown sender'}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {email.date ? new Date(email.date).toLocaleDateString() : 'No date'}
          </div>
          {email.snippet && (
            <div className="mt-2 line-clamp-2 text-xs text-gray-600">{email.snippet}</div>
          )}
          {email.labels && Array.isArray(email.labels) && email.labels.length > 0 && (
            <div className="mt-2 flex gap-1">
              {email.labels.slice(0, 3).map((label: string) => (
                <span key={label} className="rounded-full bg-gray-200 px-2 py-1 text-xs">
                  {label}
                </span>
              ))}
              {email.labels.length > 3 && (
                <span className="px-2 py-1 text-xs text-gray-500">
                  +{email.labels.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function GmailFindEmailDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(gmailFindEmail, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const emails = Array.isArray(result) ? result : result ? [result] : []

  if (emails.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No emails found</div>
        </div>
      </div>
    )
  }

  if (emails.length === 1) {
    return <EmailItem email={emails[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Search className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Found Emails</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {emails.length} emails
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {emails.map((email, index) => (
            <EmailItem key={email.id || index} email={email} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGmailFindEmail(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function gmailFindEmailToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(gmailFindEmail, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const emails = Array.isArray(result) ? result : result ? [result] : []

  if (emails.length === 0) {
    return `## Gmail Search Results

No emails found.

---
`
  }

  const emailsMarkdown = emails
    .map((email: any) => {
      return `### ${email.subject || 'No subject'}

**From:** ${email.from || email.sender || 'Unknown'}
**Date:** ${email.date ? new Date(email.date).toLocaleDateString() : 'No date'}
**Unread:** ${email.unread ? 'Yes' : 'No'}
${email.labels && Array.isArray(email.labels) && email.labels.length > 0 ? `**Labels:** ${email.labels.join(', ')}` : ''}

${email.snippet || ''}
`
    })
    .join('\n---\n\n')

  return `## Gmail Search Results (${emails.length} emails)

${emailsMarkdown}

---
`
}

export const GmailFindEmailDisplay = createWorkflowToolComponent(
  GmailFindEmailDisplayComponent,
  downloadGmailFindEmail,
  gmailFindEmailToMarkdown
)
